import React, { useMemo } from 'react';
import { Col, Row } from 'antd';
import { FormikProps } from 'formik';
import { Input } from 'formik-antd';

import FormItem from 'components/formik/FormItem';
import { WorkerInfoFormValues } from '../CompanySection';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  countriesSelector,
  countryCitiesSelector,
  isCountriesLoadingSelector,
  isCountryCitiesLoadingSelector,
} from 'redux/selectors/regionSelectors';

type Props = {
  formik: FormikProps<WorkerInfoFormValues>;
  isAddCompanyOptionSelected: boolean;
};

export default function InternationalCompanyStateForm(props: Props) {
  const { formik, isAddCompanyOptionSelected } = props;

  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);

  const countryCities = useAppSelector(countryCitiesSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);

  const filteredInternationalCities = useMemo(
    () =>
      countryCities.filter(
        (city) => city.countryId === formik.values.company.country,
      ),
    [countryCities, formik.values.company.country],
  );

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<WorkerInfoFormValues>,
  ) => {
    formik.setFieldValue('company.city', '', false);
    formik.setFieldValue('company.state', undefined, false);
    formik.setFieldValue('company.internationalCityName', '', false);
    formik.setFieldValue('company.internationalStateName', '', false);
  };

  return (
    <>
      <Row gutter={40}>
        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <FormItem name="company.country" label="Country *">
                <FormikSelectWithLoading
                  isLoading={isCountriesLoading}
                  name="company.country"
                  showSearch
                  disabled={!isAddCompanyOptionSelected}
                  placeholder="Select country"
                  optionFilterProp="children"
                  onChange={(value: number) =>
                    handleCountryChange(value, formik)
                  }
                >
                  {countries.map((country) => (
                    <FormikSelectWithLoading.Option
                      key={`select-country-${country.id}`}
                      value={country.id || -1}
                    >
                      {country.name}
                    </FormikSelectWithLoading.Option>
                  ))}
                </FormikSelectWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem name="company.internationalStateName" label="State *">
                <Input
                  name="company.internationalStateName"
                  placeholder="Enter state"
                  disabled={
                    !formik.values.company.country ||
                    !isAddCompanyOptionSelected
                  }
                  autoComplete="off"
                />
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem label="ZIP" name="company.zip">
                <Input
                  name="company.zip"
                  placeholder="Enter ZIP code"
                  disabled={!isAddCompanyOptionSelected}
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>
        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <FormItem name="company.internationalCityName" label="City *">
                <FormikAutoCompleteWithLoading
                  isLoading={isCountryCitiesLoading}
                  name="company.internationalCityName"
                  filterOption
                  placeholder="Enter city"
                  showSearch
                  disabled={
                    !formik.values.company.internationalStateName ||
                    !isAddCompanyOptionSelected
                  }
                >
                  {filteredInternationalCities.map((city) => (
                    <FormikAutoCompleteWithLoading.Option
                      key={`company-city-${city.id}`}
                      value={city.name || ''}
                    >
                      {city.name}
                    </FormikAutoCompleteWithLoading.Option>
                  ))}
                </FormikAutoCompleteWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem label="Address *" name="company.address">
                <Input
                  name="company.address"
                  placeholder="Enter address"
                  disabled={!isAddCompanyOptionSelected}
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}
